import { memo, useCallback, useEffect, useState } from "react";
import PlaylistItem from "./playlistItem/PlaylistItem";
import ListView from "../../components/listView/ListView";
import useKeydown from "../../hooks/useKeydown";
import Text from "../../components/text/Text";
import "./PlaylistPage.scss";
import useStore from "../../hooks/useStore";
import AddPlaylistPopup from "../../popups/addPlaylist/AddPlaylistPopup";
import Button from "../../components/button/Button";
import Toast from "../../components/toast/Toast";
import PlaylistLoading from "./playlistLoading/PlaylistLoading";
import XTREAM from "../../services/api/xtream";
import PlaylistStorage, { syncPlaylistsToStore } from "../../services/playlists";
import FooterInfo from "../../components/footerInfo/FooterInfo";

import { plusBlackIcon, plusWhiteIcon } from "@/src/assets/icons";

const PlaylistPage = ({ onBack = () => { } }) => {

    const [playlists] = useStore("playlists");
    const [current, setCurrent] = useState("playlist");

    const [playlistLoading, setPlaylistLoading] = useState(false);

    // the list is this device's own: nothing is fetched, this only shows what the playlist service holds
    const getPlaylists = async () => {

        try {
            await syncPlaylistsToStore();
        } catch (msg) {
            Toast.error({ msg });
        }

    }

    const hideLoading = () => {
        XTREAM.abortAll();
        setPlaylistLoading(false);
    }

    const onFocusPlaylist = useCallback(() => {
        setCurrent("playlist");
    }, []);

    const onDeletePlaylist = useCallback(async (playlist) => {

        let result = await PlaylistStorage.remove(playlist.id);

        if (result.success === false) return Toast.error({ msg: result.message });

        Toast.success({ msg: "playlist deleted successfully", time: 2000 });

    }, [playlists]);

    const onSelectPlaylist = useCallback(async (playlist) => {

        if (playlistLoading) return;

        let xtream_account = XTREAM.isXtreamPlaylist(playlist.url);

        // only Xtream playlists can be played: the selection stays and no request goes out
        if (!xtream_account) return Toast.error({ msg: "invalid playlist url", id: "add-playlist" });

        setPlaylistLoading(true);

        try {

            await XTREAM.login(xtream_account);

            let result = await PlaylistStorage.select(playlist.id);

            hideLoading();

            if (result.success === false) return Toast.error({ msg: result.message, id: "add-playlist" });

            Toast.success({ msg: "playlist selected successfully", time: 1000 });

        } catch (msg) {
            hideLoading();
            Toast.error({ msg, id: "add-playlist" });
        }

    }, [playlists]);

    const playlistItem = useCallback((props) => {

        return <PlaylistItem
            {...props}
            onFocus={onFocusPlaylist}
            onDelete={onDeletePlaylist}
            onClick={onSelectPlaylist}
        />

    }, [playlists]);

    const showAddPlaylistPopup = useCallback(() => {
        setCurrent("addPlaylistPopup");
    }, []);

    const hideAddPlaylistPopup = useCallback(() => {

        if (playlists.length) {
            setCurrent("addBtn");
        } else {
            onBack();
        }

    }, [playlists]);

    const playlistUp = useCallback(() => {
        requestAnimationFrame(() => {
            setCurrent("addBtn");
        });
    }, []);

    useKeydown({
        isActive: current == "addBtn",
        debounce: 100,
        back: () => {
            onBack();
        },
        down: () => {
            if (playlists.length) {
                setCurrent("playlist");
            }
        }
    });

    useEffect(() => {
        if (!playlists.length) {
            setCurrent("addPlaylistPopup");
        } else {
            if (current == "addPlaylistPopup") setCurrent("addBtn");
        }
    }, [playlists]);

    useEffect(() => {
        getPlaylists();
    }, []);

    return (

        <div className="playlist-page">

            {playlistLoading && <PlaylistLoading
                status="loading"
                onBack={() => { hideLoading(); }}
            />}

            {current == "addPlaylistPopup" && <AddPlaylistPopup onBack={hideAddPlaylistPopup} />}

            {current != "addPlaylistPopup" && <div className="container">

                <div className="header">
                    <div className="title">
                        <Text>playlists</Text>
                    </div>
                    <Button
                        className="add-button focus-item"
                        isActive={current == "addBtn"}
                        onClick={showAddPlaylistPopup}
                        onMouseEnter={() => { setCurrent("addBtn") }}
                    >
                        <img src={current == "addBtn" ? plusBlackIcon : plusWhiteIcon} />
                    </Button>
                </div>

                <div className="playlist-list">
                    <ListView
                        uniqueKey={"playlist-list-view"}
                        listType="vertical"
                        itemsTotal={playlists.length}
                        itemsCount={7}
                        buffer={2}
                        initialActiveIndex={0}
                        startScrollIndex={5}
                        nativeControle={true}
                        debounce={100}
                        ItemRenderer={playlistItem}
                        itemWidth={100}
                        itemHeight={12}
                        isActive={current == "playlist"}
                        onUp={playlistUp}
                        onDown={() => { }}
                        onLeft={() => { }}
                        onRight={() => { }}
                        onBack={onBack}
                    />
                </div>

            </div>}

            <FooterInfo />

        </div>
    )

}

export default memo(PlaylistPage);